import { useParams, Link, useLocation } from 'react-router-dom'
import { ArrowLeft, MapPin, Globe, Building2, Clock, Phone, CheckCircle2, XCircle, Layers } from 'lucide-react'
import { findCdcBySlug } from '../../data/cdcAmbiti'
import { AMBITI_STATS } from '../../data/ambitiStats'
import DistrictMap from '../DistrictMap'
import BSDCard from '../BSDCard'

function formatEuro(value: number) {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value)
}

export default function CdCDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const location = useLocation()
  const state = location.state as { from?: string } | null
  const backTo = state?.from === '/casedellacomunita' ? '/casedellacomunita' : '/'
  const entry = slug ? findCdcBySlug(slug) : undefined

  if (!entry) {
    return (
      <div className="cdc-detail-page">
        <div className="cdc-detail-container">
          <Link to={backTo} className="cdc-detail-back"><ArrowLeft size={16} /> Torna</Link>
          <h1>Casa della Comunità non trovata</h1>
        </div>
      </div>
    )
  }

  const { casa, ambitoId, pnrrInvestimento } = entry
  const ambito = AMBITI_STATS.find(a => a.id === ambitoId)
  const totalServizi = casa.sezioni.reduce((acc, s) => acc + s.servizi.length, 0)

  return (
    <div className="cdc-detail-page">
      <div className="cdc-detail-container">
        <Link to={backTo} className="cdc-detail-back"><ArrowLeft size={16} /> Torna</Link>

        {/* Header */}
        <div className="cdc-detail-header">
          <h1 className="cdc-detail-title">{casa.nome}</h1>
          <div className="cdc-detail-meta">
            <span className={`cdc-detail-badge ${casa.attiva === 1 ? 'cdc-detail-badge--active' : 'cdc-detail-badge--inactive'}`}>
              {casa.attiva === 1 ? <><CheckCircle2 size={14} /> Aperta</> : <><XCircle size={14} /> Chiusa</>}
            </span>
            {ambito && (
              <span className="cdc-detail-ambito-tag">
                <Layers size={12} /> {ambito.nome}
              </span>
            )}
          </div>
        </div>

        {/* Info + Map row */}
        <div className="cdc-detail-row">
          <BSDCard kind="custom" className="cdc-detail-info-card" sezione="Casa della Comunità">
            <h2 className="cdc-detail-section-title"><Building2 size={16} /> Informazioni</h2>
            <dl className="cdc-detail-dl">
              <div><dt>Indirizzo</dt><dd><MapPin size={13} /> {casa.indirizzo}</dd></div>
              <div><dt>Città</dt><dd>{casa.citta}</dd></div>
              {casa.sezioneTerritoriale && <div><dt>Distretto</dt><dd>{casa.sezioneTerritoriale}</dd></div>}
              {casa.sezioneDistrettuale && <div><dt>ASST</dt><dd>{casa.sezioneDistrettuale}</dd></div>}
              <div><dt>Investimento PNRR</dt><dd><strong>{formatEuro(pnrrInvestimento)}</strong></dd></div>
              {casa.sitoWeb && (
                <div><dt>Sito web</dt><dd><Globe size={13} /> <a href={`https://${casa.sitoWeb}`} target="_blank" rel="noreferrer">{casa.sitoWeb}</a></dd></div>
              )}
              {ambito && <div><dt>Ambito</dt><dd>{ambito.nome} ({ambito.numeroComuni} comuni, {ambito.popolazione.toLocaleString('it-IT')} ab.)</dd></div>}
            </dl>
          </BSDCard>

          <BSDCard kind="custom" className="cdc-detail-map-card" sezione="Casa della Comunità">
            <h2 className="cdc-detail-section-title"><MapPin size={16} /> Mappa del distretto</h2>
            <DistrictMap
              onSelectDistrict={() => {}}
              onClearSelection={() => {}}
              showCdcMarkers
              showComuneCountInLegend={false}
              selectedDistrictId={ambitoId}
              allowDistrictSelection={false}
              showInstructionText={false}
              selectedCdcNome={casa.nome}
            />
          </BSDCard>
        </div>

        {/* Servizi */}
        <BSDCard kind="custom" className="cdc-detail-services-card" sezione="Casa della Comunità">
          <h2 className="cdc-detail-section-title">
            <Building2 size={16} /> Servizi
            <span className="cdc-detail-services-count">{totalServizi}</span>
          </h2>
          <div className="cdc-detail-sections">
            {casa.sezioni.map(sezione => (
              <div key={sezione.id} className="cdc-detail-sezione">
                <h3 className="cdc-detail-sezione-title">{sezione.titolo}</h3>
                <div className="cdc-detail-servizi-grid">
                  {sezione.servizi.map(servizio => (
                    <div key={servizio.id} className="cdc-detail-servizio">
                      <h4 className="cdc-detail-servizio-nome">{servizio.nome}</h4>
                      <p className="cdc-detail-servizio-desc">{servizio.descrizione}</p>
                      {servizio.orari && servizio.orari.length > 0 && (
                        <div className="cdc-detail-servizio-block">
                          <Clock size={13} /> <strong>Orari</strong>
                          <ul>{servizio.orari.map((o, i) => <li key={i}>{o}</li>)}</ul>
                        </div>
                      )}
                      {servizio.contatti && servizio.contatti.length > 0 && (
                        <div className="cdc-detail-servizio-block">
                          <Phone size={13} /> <strong>Contatti</strong>
                          <ul>{servizio.contatti.map((c, i) => <li key={i}>{c}</li>)}</ul>
                        </div>
                      )}
                      {servizio.accesso && (
                        <div className="cdc-detail-servizio-block">
                          <strong>Accesso:</strong> {servizio.accesso}
                        </div>
                      )}
                      {servizio.prestazioni && servizio.prestazioni.length > 0 && (
                        <div className="cdc-detail-servizio-block">
                          <strong>Prestazioni</strong>
                          <ul>{servizio.prestazioni.map((p, i) => <li key={i}>{p}</li>)}</ul>
                        </div>
                      )}
                      {servizio.note && servizio.note.length > 0 && (
                        <div className="cdc-detail-servizio-block cdc-detail-servizio-note">
                          {servizio.note.map((n, i) => <p key={i}>{n}</p>)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </BSDCard>
      </div>
    </div>
  )
}
